import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Award, Search, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCertificates() {
    const [certs, setCerts] = useState([]);
    const [q, setQ] = useState('');
    const [confirm, setConfirm] = useState(null);
    const [busy, setBusy] = useState(false);
    const del = async (c) => {
        setBusy(true);
        try {
            await api.delete(`/admin/certificates/${c.id}`);
            toast.success('Certificate deleted');
            setCerts((prev) => prev.filter((x) => x.id !== c.id));
        } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
        finally { setBusy(false); setConfirm(null); }
    };
    useEffect(() => {
        // Admin can fetch certs via /admin/enrollments + certificates endpoint
        api.get('/admin/enrollments').then(async () => {
            // For simplicity, list all certs in DB by hitting trainee/certificates per user — but use a simpler aggregation:
            // We'll fetch certs by calling /api/admin/enrollments and then known cert endpoint via DB. Workaround: use a server endpoint listing all certs.
        });
        // Use a direct call to list-all-certs by enumerating users would be too much; use a simple list via /api/admin/enrollments to derive
        // Cleanest: call /api/admin/reports/course-completion and /api/admin/dashboard/kpis for count, plus query DB via a helper:
        // Add a simpler endpoint: GET /api/certificates (admin)
        api.get('/admin/certificates').then((r) => setCerts(r.data)).catch(() => setCerts([]));
    }, []);
    const filtered = certs.filter((c) => !q || c.user_name?.toLowerCase().includes(q.toLowerCase()) || c.course_title?.toLowerCase().includes(q.toLowerCase()) || c.certificate_id?.toLowerCase().includes(q.toLowerCase()));
    return (
        <div data-testid="admin-certificates-page">
            <h1 className="font-display font-extrabold text-3xl text-slate-900">Certificates</h1>
            <p className="text-sm text-slate-500 mt-1 mb-5">All certificates issued across the academy.</p>
            <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-5">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by learner, course, ID…" className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm" data-testid="certs-search" />
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                {filtered.map((c) => (
                    <div key={c.id} className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5 relative overflow-hidden grain-overlay" data-testid={`cert-${c.certificate_id}`}>
                        <button onClick={() => setConfirm(c)} title="Delete certificate"
                            className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/10 hover:bg-rose-600 text-white/80 hover:text-white transition"
                            data-testid={`cert-delete-${c.certificate_id}`}>
                            <Trash2 className="h-4 w-4" />
                        </button>
                        <Award className="h-7 w-7 text-brand-orange" />
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-3">Propulse Cables & Wires Academy</div>
                        <div className="font-display font-bold text-lg leading-tight mt-1">{c.course_title}</div>
                        <div className="text-sm text-slate-300 mt-3">Awarded to</div>
                        <div className="font-display font-extrabold text-xl">{c.user_name}</div>
                        <div className="flex items-center justify-between mt-4 text-[10px] text-slate-400">
                            <div>ID: <span className="font-mono">{c.certificate_id}</span></div>
                            <div>Score: {c.score?.toFixed(0) || 0}%</div>
                        </div>
                    </div>
                ))}
                {!filtered.length && <div className="col-span-2 text-center py-10 text-sm text-slate-500">No certificates yet. They'll appear here as learners complete courses.</div>}
            </div>

            {confirm && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setConfirm(null)} data-testid="confirm-delete-cert">
                    <div className="bg-white rounded-3xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start gap-3">
                            <span className="grid place-items-center w-10 h-10 rounded-full bg-rose-100 text-rose-600 shrink-0"><AlertTriangle className="h-5 w-5" /></span>
                            <div>
                                <h3 className="font-display font-bold text-lg text-slate-900">Delete this certificate?</h3>
                                <p className="text-sm text-slate-600 mt-1"><b>{confirm.course_title}</b> for <b>{confirm.user_name}</b> (ID {confirm.certificate_id}) will be permanently removed. This cannot be undone.</p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={() => setConfirm(null)} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="cert-confirm-cancel">Cancel</button>
                            <button onClick={() => del(confirm)} disabled={busy} className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-60" data-testid="cert-confirm-delete">
                                {busy ? 'Deleting…' : 'Delete certificate'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
